import { useMemo, useState } from "react";

const buttonClass =
    "cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[4px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-not-allowed disabled:opacity-50";
const textEncoder = new TextEncoder();

function findingKey(finding) {
    return `${finding.kind}\u0000${finding.jsonPointer}`;
}

export default function SecretFindingOverrideDialog({
    title = "Review possible false positives",
    description = "Secret-like content was found. Matched values remain hidden; only categories and paths appear here. Approval expires when the content or detection rules change.",
    confirmLabel = "Approve all and publish",
    busyLabel = "Publishing…",
    findings = [],
    contentDigest,
    detectorVersion,
    busy = false,
    errorMessage = "",
    onCancel,
    onConfirm,
}) {
    const [approvals, setApprovals] = useState({});
    const complete = useMemo(
        () => findings.length > 0 && findings.every((finding) => {
            const value = approvals[findingKey(finding)];
            if (typeof value !== "string") return false;
            const bytes = textEncoder.encode(value.trim()).byteLength;
            return bytes >= 8 && bytes <= 500;
        }),
        [approvals, findings],
    );

    const setFinding = (finding, value) => {
        const key = findingKey(finding);
        setApprovals((current) => {
            const next = { ...current };
            if (value === null) delete next[key];
            else next[key] = value;
            return next;
        });
    };

    const confirm = () => {
        if (!complete || busy) return;
        onConfirm(findings.map((finding) => ({
            kind: finding.kind,
            jsonPointer: finding.jsonPointer,
            contentDigest,
            detectorVersion,
            justification: approvals[findingKey(finding)].trim(),
        })));
    };

    return (
        <div
            className="fixed inset-0 z-[130] grid place-items-center bg-black/45 px-[18px] py-[24px]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="secret-override-title"
        >
            <div className="grid max-h-full w-full max-w-[620px] gap-[13px] overflow-auto rounded-[3px] border border-amber-400/40 bg-[color:var(--panel)] px-[18px] py-[16px] shadow-2xl">
                <header className="grid gap-[4px]">
                    <h2
                        id="secret-override-title"
                        className="text-[13px] font-medium text-[color:var(--ink)]"
                    >
                        {title}
                    </h2>
                    <p className="text-[11px] leading-[1.45] text-[color:var(--muted)]">
                        {description}
                    </p>
                </header>
                <div className="grid gap-[8px]">
                    {findings.map((finding) => {
                        const key = findingKey(finding);
                        const selected = Object.prototype.hasOwnProperty.call(approvals, key);
                        return (
                            <div
                                key={key}
                                className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] p-[9px]"
                            >
                                <label className="flex items-start gap-[7px] text-[11px] text-[color:var(--ink)]">
                                    <input
                                        type="checkbox"
                                        checked={selected}
                                        disabled={busy}
                                        onChange={(event) =>
                                            setFinding(finding, event.target.checked ? "" : null)}
                                        className="mt-[2px]"
                                    />
                                    <span className="min-w-0">
                                        <span className="block text-amber-300">{finding.kind}</span>
                                        <code className="block break-all text-[10px] text-[color:var(--muted)]">
                                            {finding.jsonPointer || "/"}
                                        </code>
                                    </span>
                                </label>
                                {selected ? (
                                    <textarea
                                        className="mt-[7px] min-h-[64px] w-full resize-y rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[6px] text-[11px] text-[color:var(--ink)] outline-none"
                                        value={approvals[key]}
                                        maxLength={500}
                                        disabled={busy}
                                        placeholder="Explain why this finding is safe to retain (8 to 500 characters)"
                                        onChange={(event) => setFinding(finding, event.target.value)}
                                    />
                                ) : null}
                            </div>
                        );
                    })}
                </div>
                {errorMessage ? (
                    <p role="alert" className="text-[11px] text-red-300">
                        {errorMessage}
                    </p>
                ) : null}
                <footer className="flex justify-end gap-[7px]">
                    <button type="button" className={buttonClass} onClick={onCancel} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={buttonClass}
                        onClick={confirm}
                        disabled={!complete || busy}
                    >
                        {busy ? busyLabel : confirmLabel}
                    </button>
                </footer>
            </div>
        </div>
    );
}
